import React from 'react';
import './Pages/PagesCss/PrivacyPolicy.css';

const AboutUs = () => {
  const values = [
    {
      icon: "fa-solid fa-calculator",
      title: "Accuracy",
      description: "We focus on delivering precise and reliable tax results, ensuring every calculation reflects the latest tax standards."
    },
    {
      icon: "fa-solid fa-globe",
      title: "Accessibility",
      description: "Our tools are designed to be simple, free, and accessible for everyone — individuals, professionals, and businesses."
    },
    {
      icon: "fa-solid fa-lightbulb",
      title: "Transparency",
      description: "We explain how each tool works, giving users full clarity on assumptions, logic, and methods behind every calculation."
    },
    {
      icon: "fa-solid fa-gear",
      title: "Continuous Improvement",
      description: "We constantly update our tools based on feedback, tax law changes, and evolving financial needs."
    }
  ];

  const milestones = [
    { year: "2024", title: "Foundation", description: "AfterTaxPay.net was created by D-Tech Studios to simplify U.S. tax calculations." },
    { year: "2025", title: "Launch", description: "Released our first suite of tools including payroll, income, and property tax calculators." },
    { year: "2025", title: "User Growth", description: "Reached 100,000+ monthly users across the U.S. and beyond." },
    { year: "2025", title: "Platform Expansion", description: "Added more tools, reports, and personalized tax insights for individuals and businesses." },
  ];

  return (
    <div className="privacy-policy">
      {/* Header Section */}
      <section className="policy-header">
        <div className="container">
          <div className="header-content">
            <h1>About AfterTaxPay.net</h1>
            <p className="header-description">
              Making tax calculations simple, accurate, and accessible. 
              Our mission is to help individuals, investors, and businesses 
              understand their taxes with confidence.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="policy-content">
        <div className="container">
          <div className="policy-grid">
            {/* Sidebar Navigation */}
            <aside className="table-of-contents">
              <h3>Quick Navigation</h3>
              <nav>
                <ul>
                  <li><a href="#our-story">Our Story</a></li>
                  <li><a href="#our-mission">Our Mission</a></li>
                  <li><a href="#our-values">Our Values</a></li>
                  <li><a href="#our-journey">Our Journey</a></li>
                  <li><a href="#about-dtech">About D-Tech Studios</a></li>
                  <li><a href="#contact">Contact Us</a></li>
                </ul>
              </nav>
            </aside>

            {/* Main Sections */}
            <div className="policy-sections">
              <section id="our-story" className="policy-section">
                <h2>Our Story</h2>
                <div className="section-content">
                  <h3>From Complexity to Clarity</h3>
                  <p>
                    <strong>AfterTaxPay.net</strong> was created to solve a common problem
                    tax calculations are often complicated, confusing, and time-consuming. 
                    Our goal was to make them clear, quick, and accurate.
                  </p>
                  <p>
                    Founded and developed by <strong>D-Tech Studios</strong>, the platform brings 
                    together technology and financial knowledge to help users calculate 
                    payroll, property, inheritance, and capital gains taxes in seconds.
                  </p>
                </div>
              </section>

              <section id="our-mission" className="policy-section">
                <h2>Our Mission & Vision</h2>
                <div className="section-content">
                  <h3>Our Mission</h3>
                  <p>
                    To simplify complex tax calculations through easy-to-use tools and 
                    accurate insights that help users make smarter financial decisions.
                  </p>

                  <h3>Our Vision</h3>
                  <p>
                    To become the most trusted online resource for tax tools and 
                    calculators in the U.S. and beyond — built on clarity, accuracy, 
                    and reliability.
                  </p>

                  <div className="rights-grid">
                    <div className="right-item">
                      <h4>Accuracy</h4>
                      <p>Always up-to-date with the latest tax rates and laws</p>
                    </div>
                    <div className="right-item">
                      <h4>Transparency</h4>
                      <p>Clear explanations behind every calculator and report</p>
                    </div>
                    <div className="right-item">
                      <h4>Ease of Use</h4>
                      <p>Tools designed for all users — from individuals to accountants</p>
                    </div>
                    <div className="right-item">
                      <h4>Trust</h4>
                      <p>Accurate results, reliable methods, and data privacy</p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="our-values" className="policy-section">
                <h2>Our Values</h2>
                <div className="section-content">
                  <p>
                    These principles guide how we build, improve, and support our platform every day.
                  </p>
                  <div className="values-grid">
                    {values.map((value, index) => (
                      <div key={index} className="value-item">
                        <div className="value-icon"><i className={value.icon}></i></div>
                        <div className="value-content">
                          <h4>{value.title}</h4>
                          <p>{value.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section id="our-journey" className="policy-section">
                <h2>Our Journey</h2>
                <div className="section-content">
                  <p>
                    Every milestone represents our ongoing commitment to improving the 
                    tax experience for users across the world.
                  </p>
                  <div className="timeline">
                    {milestones.map((milestone, index) => (
                      <div key={index} className="timeline-item">
                        <div className="timeline-year">{milestone.year}</div>
                        <div className="timeline-content">
                          <h4>{milestone.title}</h4>
                          <p>{milestone.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section id="about-dtech" className="policy-section">
                <h2>About D-Tech Studios</h2>
                <div className="section-content">
                  <p>
                    <strong>D-Tech Studios</strong> is a technology company that builds 
                    modern, reliable, and user-friendly digital tools. We focus on 
                    solving real problems through clean design and practical innovation.
                  </p>
                  <p>
                    Our portfolio includes applications and platforms across various 
                    domains, with a focus on AI, productivity, and financial technology.
                  </p>
                </div>
              </section>

              <section id="contact" className="policy-section">
                <h2>Contact Us</h2>
                <div className="section-content">
                  <p>
                    Have questions, suggestions, or feedback? We’d love to hear from you. 
                    Reach out anytime, and we’ll get back to you soon.
                  </p>

                  <div className="contact-info">
                    <div className="contact-item">
                      <strong>Email:</strong> support@aftertaxpay.net
                    </div>
                    <div className="contact-item">
                      <strong>Address:</strong> 2568 Madison Ave, League City, TX 77573
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="policy-actions">
        <div className="container">
          <div className="actions-content">
            <h3>Calculate Your Taxes Effortlessly</h3>
            <p>Try our free tools today and make smarter, faster tax decisions.</p>
            <div className="action-buttons">
              <a href="/PayrollCalculatorUSA" className="btn btn-primary">Explore Calculators</a>
              <a href="/Contact" className="btn btn-secondary">Contact Support</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
